"use client";

import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { useTranslations } from "next-intl";

export default function EconomicOpportunitiesPage() {
  const t = useTranslations("economicOpportunities");

  return (
    <section className="section-padding bg-white py-16 md:py-24 px-4">

      <Breadcrumb
        pageName={t("pageName")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/joinPartner.webp")]}
        bgIndex={0}
      />

      <div className="container mt-10">

        <h2 className="text-2xl md:text-3xl font-bold text-[#A23465] mb-6 md:mb-3 max-h-20">
          {t("title_benefits")}
        </h2>

        {/* ---------------------- SECTION 1 ---------------------- */}
        <div className="mt-10 flex flex-col md:flex-row gap-5">
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec1_title")}
            </h3>

            <div className="text-base space-y-7">
              <div className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t("sec1_point1")}</p>
              </div>

              <div className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t("sec1_point2")}</p>
              </div>

              <div className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t("sec1_point3")}</p>
              </div>

              <div className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t("sec1_point4")}</p>
              </div>
            </div>
          </div>

          {/* ---------------------- SECTION 2 ---------------------- */}
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec2_title")}
            </h3>

            <div className="text-base space-y-7">
              {["sec2_point1","sec2_point2","sec2_point3"].map(key => (
                <div key={key} className="flex items-start gap-3 relative">
                  <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                  <p className="ml-6">{t(key)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---------------------- SECTION 3 ---------------------- */}
        <div className="mt-10 flex flex-col md:flex-row gap-5">
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec3_title")}
            </h3>

            {["sec3_point1","sec3_point2","sec3_point3"].map(key => (
              <div key={key} className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t(key)}</p>
              </div>
            ))}
          </div>

          {/* ---------------------- SECTION 4 ---------------------- */}
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec4_title")}
            </h3>

            {["sec4_point1","sec4_point2","sec4_point3"].map(key => (
              <div key={key} className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t(key)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ---------------------- SECTION 5 ---------------------- */}
        <div className="mt-10 flex flex-col md:flex-row gap-5">
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec5_title")}
            </h3>

            {["sec5_point1","sec5_point2","sec5_point3"].map(key => (
              <div key={key} className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t(key)}</p>
              </div>
            ))}
          </div>

          {/* ---------------------- SECTION 6 ---------------------- */}
          <div className="w-full md:w-6/12">
            <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
              {t("sec6_title")}
            </h3>

            {["sec6_point1","sec6_point2","sec6_point3"].map(key => (
              <div key={key} className="flex items-start gap-3 relative">
                <p className="absolute left-0 -top-1 text-lg font-bold text-[#A23465]">●</p>
                <p className="ml-6">{t(key)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ---------------------- SUMMARY TABLE ---------------------- */}
        <div className="mt-10 gap-5">
          <h3 className="text-lg md:text-2xl font-semibold mb-2 text-[#A23465]">
            {t("summary_title")}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Column 1 */}
            <div className="w-full">
              <ul className="list-none text-base">
                <li className="bg-[#A23465] text-white font-semibold px-4 py-2 mb-2">
                  {t("table_col1_title")}
                </li>

                {[
                  "table_col1_row1",
                  "table_col1_row2",
                  "table_col1_row3",
                  "table_col1_row4",
                  "table_col1_row5",
                  // "table_col1_row6"
                ].map(key => (
                  <li key={key} className="bg-[#FCEDF4] px-4 py-2 mb-2">
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2 */}
            <div className="w-full">
              <ul className="list-none text-base">
                <li className="bg-[#A23465] text-white font-semibold px-4 py-2 mb-2">
                  {t("table_col2_title")}
                </li>

                {[
                  "table_col2_row1",
                  "table_col2_row2",
                  "table_col2_row3",
                  "table_col2_row4",
                  "table_col2_row5"
                  // "table_col2_row6"
                ].map(key => (
                  <li key={key} className="bg-[#FCEDF4] px-4 py-2 mb-2">
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3 */}
            <div className="w-full">
              <ul className="list-none text-base">
                <li className="bg-[#A23465] text-white font-semibold px-4 py-2 mb-2">
                  {t("table_col3_title")}
                </li>

                {[
                  "table_col3_row1",
                  "table_col3_row2",
                  "table_col3_row3",
                  "table_col3_row4",
                  "table_col3_row5",
                  
                ].map(key => (
                  <li key={key} className="bg-[#FCEDF4] px-4 py-2 mb-2">
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>

          </div>

          <p className="mt-6 text-base font-normal">
            {t("summary_footer")}
          </p>
        </div>

      </div>
    </section>
  );
}
